import React from 'react';
import { Menu } from 'antd';
import { useLocation } from 'react-router';
import { FeatureFlags } from '@shared/utils/constants';
import useFeatureFlag from '@client/utils/hooks/useFeatureFlag';
import { getDepartmentMenuItems } from '../helpers/departmentMenuItems';
import buildMenuItemComponents, { BuildableMenuItem } from '../helpers/buildMenuItemComponents';
import getMenuItems from '../helpers/getMenuItems';
import styles from './NavbarMenu.module.css';

type NavbarMenuProps = {
	activeDepartment: string;
	isAdmin: boolean;
};

const NavbarMenu = ({
	activeDepartment,
	isAdmin,
}: NavbarMenuProps) => {
	const location = useLocation();
	const showPerformance = useFeatureFlag(FeatureFlags.PERFORMANCE);

	const menuItems = getMenuItems({ showPerformance, showSpotFixtures: false });
	const departmentMenuItemKeys = getDepartmentMenuItems(activeDepartment);
	const departmentMenuItems: BuildableMenuItem[] = departmentMenuItemKeys.map((k) => menuItems[k]);

	const availableMenuItems = departmentMenuItems.filter((item) => !item.admin || isAdmin);

	const flattenMenu = (items: BuildableMenuItem[]): BuildableMenuItem[] => items
		.flatMap((item) => [item, ...(item.children ? flattenMenu(item.children) : [])]);

	const fullPath = `${location.pathname}${location.search}`;

	const isActiveLink = (link: string) => {
		if (link === '/') {
			return location.pathname === '/';
		}

		// If link contains query params, compare against full URL
		if (link.includes('?')) {
			return fullPath.startsWith(link);
		}

		// Otherwise compare only pathname
		return location.pathname.startsWith(link);
	};

	const activeKeys = flattenMenu(availableMenuItems)
		.filter((item) => item.link != null && isActiveLink(item.link))
		.map((item) => item.link as string);

	return (
		<Menu
			mode="inline"
			theme="dark"
			selectedKeys={activeKeys}
			className={styles.menu}
			items={buildMenuItemComponents(availableMenuItems)}
		/>
	);
};

export default NavbarMenu;
